import React, { useState } from 'react';
import { 
    Button, 
    Modal, 
    ModalDialog, 
    ModalClose, 
    DialogTitle, 
    Stack, 
    Typography, 
    List, 
    ListItem, 
    ListItemContent, 
    ListItemButton,
    IconButton,
    Input,
    Box,
    Chip,
    Divider
} from '@mui/joy';
import { 
    Delete, 
    Search
} from '@mui/icons-material';
import { useObjectCache } from '../hooks/useObjectCache';

export default function ObjectCacheSelector({ 
    open, 
    onClose, 
    onSelect, 
    schema, 
    schemaKey,
    title = "Select Cached Object"
}) {
    const { getCachedObjects, deleteFromCache, deepClone } = useObjectCache();
    const [searchTerm, setSearchTerm] = useState('');

    const cachedObjects = getCachedObjects(schemaKey);
    const cacheKeys = Object.keys(cachedObjects);

    // Filter cached objects based on search term (search in values)
    const filteredKeys = cacheKeys.filter(key => {
        const obj = cachedObjects[key];
        const searchableText = JSON.stringify(obj).toLowerCase();
        return searchableText.includes(searchTerm.toLowerCase());
    });

    const handleSelect = (key) => {
        const cachedValue = cachedObjects[key];
        onSelect(deepClone(cachedValue));
        onClose();
    };

    const handleDelete = (key, event) => {
        event.stopPropagation();
        deleteFromCache(schemaKey, key);
    };

    // Format object values for display (two levels deep)
    const formatObjectForDisplay = (obj, level = 0) => {
        if (level > 1) return '...'; // Limit to two levels
        
        const entries = Object.entries(obj);
        if (entries.length === 0) return 'Empty object';
        
        return entries
            .map(([key, value]) => {
                if (typeof value === 'number') {
                    return `${key}: ${value}`;
                } else if (typeof value === 'string') {
                    return `${key}: "${value}"`;
                } else if (typeof value === 'boolean') {
                    return `${key}: ${value}`;
                } else if (Array.isArray(value)) {
                    return `${key}: [${value.length} items]`;
                } else if (typeof value === 'object' && value !== null) {
                    // Recursively format nested objects
                    const nestedContent = formatObjectForDisplay(value, level + 1);
                    return `${key}: {${nestedContent}}`;
                }
                return `${key}: ${value}`;
            })
            .join(', ');
    };

    return (
        <>
            <Modal 
                open={open} 
                onClose={onClose}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <ModalDialog sx={{ maxWidth: 600, maxHeight: '80vh' }}>
                    <ModalClose onClick={onClose} />
                    <DialogTitle>{title}</DialogTitle>
                    
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        {/* Search Controls */}
                        <Input
                            startDecorator={<Search />}
                            placeholder="Search cached objects by values..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ width: '100%' }}
                        />

                        <Divider />

                        {/* Cached Objects List */}
                        {filteredKeys.length > 0 ? (
                            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                {filteredKeys.map((key) => (
                                    <ListItem key={key} sx={{ p: 0, mb: 1 }}>
                                        <ListItemButton 
                                            onClick={() => handleSelect(key)}
                                            sx={{ 
                                                borderRadius: 'md',
                                                p: 2,
                                                minHeight: 80,
                                                '&:hover': {
                                                    backgroundColor: 'primary.50'
                                                }
                                            }}
                                        >
                                            <ListItemContent>
                                                <Typography level="body-sm" sx={{ 
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.8rem',
                                                    lineHeight: 1.4,
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                    {formatObjectForDisplay(cachedObjects[key])}
                                                </Typography>
                                            </ListItemContent>
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="danger"
                                                onClick={(e) => handleDelete(key, e)}
                                                sx={{ ml: 1, alignSelf: 'flex-start' }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Box sx={{ 
                                p: 3, 
                                textAlign: 'center',
                                backgroundColor: 'neutral.50',
                                borderRadius: 'md',
                                border: '1px dashed',
                                borderColor: 'neutral.300'
                            }}>
                                <Typography level="body-sm" color="neutral">
                                    {searchTerm ? 'No cached objects match your search' : 'No cached objects available'}
                                </Typography>
                                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                                    Objects are automatically cached when you configure them
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </ModalDialog>
            </Modal>
        </>
    );
}
